import React, { useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { FilterButton, SimpleList } from '@app/components';
import { StatusDot } from '@app/components/decor/StatusDot';
import { SvgEye } from '@app/components/icons/SvgEye';
import { useTable } from '@app/hooks/useTable';
import { fiscalApi } from '@app/store';
import { useAppSelector } from '@app/store/hooks';
import { theme } from '@app/theme';
import type { IFiscalRegisterSummary } from '@tickeat/types';
import { COULEUR_ETAT } from './couleurs';
import { couleurEtatRegistre, filtrerRegistres, libelleEtatRegistre, nombreDeRegistresHorsService, suffixesEtatRegistre } from './fiscal.logic';

const columnHelper = createColumnHelper<IFiscalRegisterSummary>();
const dateFr = (iso: string | null) => (iso ? DateTime.fromISO(iso).toFormat('dd/MM/yyyy HH:mm') : '—');

interface Props {
  selectedRowItem?: Partial<IFiscalRegisterSummary>;
  onRowPress: (item: IFiscalRegisterSummary) => void;
  onShow: (item: IFiscalRegisterSummary) => void;
}

// Actives d'abord, puis par code : la caisse qui vend est celle qu'on cherche.
const trier = (a: IFiscalRegisterSummary, b: IFiscalRegisterSummary) =>
  (a.status === 'ACTIVE' ? 0 : 1) - (b.status === 'ACTIVE' ? 0 : 1) || a.registerCode.localeCompare(b.registerCode);

export const FiscalRegisterList = ({ selectedRowItem, onRowPress, onShow }: Props) => {
  // Les caisses sont propres à une boutique et la gateway la porte par
  // l'en-tête `x-shop` : sans boutique, la requête revenait en « Bad Request
  // Exception » (retour du 9 sept.). L'écran rend alors un état dédié.
  const boutique = useAppSelector((state) => state.app.selectedShop);

  // La liste s'ouvre sur les caisses ACTIVE. Les registres retirés, abandonnés
  // et disparus restent consultables (ils gardent leurs archives), mais derrière
  // un bouton : sans ça, onze registres morts noyaient les trois qui vendent.
  const [avecHistorique, setAvecHistorique] = useState(false);

  const columns = [
    columnHelper.accessor('registerCode', {
      header: () => <SimpleList.HeaderItem width="14%"><SimpleList.HeaderItemLabel>Caisse</SimpleList.HeaderItemLabel></SimpleList.HeaderItem>,
      cell: (info) => <SimpleList.ItemCol width="14%"><SimpleList.ItemText align="left" bold active={info.active}>{info.getValue()}</SimpleList.ItemText></SimpleList.ItemCol>,
    }),
    columnHelper.accessor('terminalId', {
      header: () => <SimpleList.HeaderItem width="14%"><SimpleList.HeaderItemLabel>Terminal</SimpleList.HeaderItemLabel></SimpleList.HeaderItem>,
      cell: (info) => <SimpleList.ItemCol width="14%"><SimpleList.ItemText align="left" active={info.active}>{info.getValue().slice(0, 8)}</SimpleList.ItemText></SimpleList.ItemCol>,
    }),
    columnHelper.accessor('status', {
      header: () => <SimpleList.HeaderItem width="20%"><SimpleList.HeaderItemLabel>État</SimpleList.HeaderItemLabel></SimpleList.HeaderItem>,
      cell: (info) => (
        <SimpleList.ItemCol width="20%">
          <SimpleList.ItemImageWrapper>
            <StatusDot color={COULEUR_ETAT[couleurEtatRegistre(info.getValue())]} />
            <SimpleList.ItemText align="left" active={info.active}>
              {' '}{libelleEtatRegistre(info.getValue())}{suffixesEtatRegistre(info.row.original)}
            </SimpleList.ItemText>
          </SimpleList.ItemImageWrapper>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.accessor('lastRecordAt', {
      header: () => <SimpleList.HeaderItem width="20%"><SimpleList.HeaderItemLabel>Dernière réplication</SimpleList.HeaderItemLabel></SimpleList.HeaderItem>,
      cell: (info) => <SimpleList.ItemCol width="20%"><SimpleList.ItemText align="left" active={info.active}>{dateFr(info.getValue())}</SimpleList.ItemText></SimpleList.ItemCol>,
    }),
    columnHelper.accessor('lastDailyClosureIso', {
      header: () => <SimpleList.HeaderItem width="20%"><SimpleList.HeaderItemLabel>Dernier Z</SimpleList.HeaderItemLabel></SimpleList.HeaderItem>,
      cell: (info) => <SimpleList.ItemCol width="20%"><SimpleList.ItemText align="left" active={info.active}>{dateFr(info.getValue())}</SimpleList.ItemText></SimpleList.ItemCol>,
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <SimpleList.HeaderItem width="12%" align="center"><SimpleList.HeaderItemLabel>Actions</SimpleList.HeaderItemLabel></SimpleList.HeaderItem>,
      cell: ({ row, active }) => (
        <SimpleList.ItemCol width="12%">
          <SimpleList.ItemActions>
            <SimpleList.ItemAction onPress={() => onShow(row.original)}>
              <SvgEye color={active ? '#FFF' : theme.colors.celadonblue} />
            </SimpleList.ItemAction>
          </SimpleList.ItemActions>
        </SimpleList.ItemCol>
      ),
    }),
  ];

  // La requête est appelée ICI et non dans `useQuery` : le bouton a besoin du
  // nombre de caisses hors service, qui se compte sur la liste ENTIÈRE, avant
  // le filtre.
  const registres = fiscalApi.useReadFiscalRegistersQuery(undefined, { skip: !boutique });
  const horsService = nombreDeRegistresHorsService(registres.data ?? []);

  // `useTable` accepte un tableau simple (pas de pagination côté serveur :
  // une boutique a quelques caisses). `useQuery` est appelé avec `search` et
  // `pagination`, ignorés ici.
  const { table, isFetching } = useTable<IFiscalRegisterSummary>({
    columns,
    // `SimpleList` compare `item.original['_id']` à `selectedRowItem._id`
    // pour la ligne active, et `useResourceScreenHelpers` se réinitialise
    // sur `_id` : le registre n'en a pas, on l'aliase sur `registerId`.
    // Le `as never` n'existe que parce que `useQuery` est typé pour une
    // réponse paginée ; `useTable` accepte le tableau nu.
    useQuery: () => ({
      ...registres,
      data: filtrerRegistres([...(registres.data ?? [])].sort(trier), avecHistorique).map((r) => ({ ...r, _id: r.registerId })),
    }) as never,
  });

  // Le champ « Recherche.. » de `SimpleList` est INERTE ici : aucun `onSearch`
  // n'est passé (une boutique a quelques caisses, tout tient à l'écran). La
  // place qu'il occupait sert au dépliage de l'historique — et reste vide quand
  // aucune caisse n'est hors service, il n'y aurait rien à déplier.
  return (
    <SimpleList
      table={table}
      isFetching={isFetching}
      SearchComponent={horsService === 0 ? <React.Fragment /> : (
        <FilterButton
          testID="basculer-historique-caisses"
          title={avecHistorique ? 'Masquer les caisses retirées et disparues' : `Afficher les caisses retirées et disparues (${horsService})`}
          active={avecHistorique}
          onPress={() => setAvecHistorique((v) => !v)} />
      )}
      selectedRowItem={selectedRowItem}
      onRowPress={onRowPress} />
  );
};
